import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Landmark, Lightbulb, NotebookPen, RefreshCw, User } from "lucide-react";
import { toast } from "sonner";
import { CASES, NEXT_LINES, STAGES } from "@/data/demo";
import { useAuth } from "@/lib/auth";
import { Btn, Card, StageBadge } from "@/components/common";
import { CaseJourney } from "@/components/CaseJourney";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/10 text-amber-400"><Icon size={16} /></div>
    <div><div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div><div className="text-sm font-semibold text-white">{value}</div></div>
  </div>
);

const StageDialog = ({ open, onOpenChange, current }) => {
  const [value, setValue] = useState(String(current));
  const save = () => {
    onOpenChange(false);
    toast.success(`Stage set to "${STAGES[Number(value)].title}"`, { description: "Demo mode — nothing was saved." });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="update-stage-modal" className="rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy">Update case stage</DialogTitle>
          <DialogDescription>Choose the new stage for this case.</DialogDescription>
        </DialogHeader>
        <Select value={value} onValueChange={setValue}>
          <SelectTrigger data-testid="update-stage-dropdown" className="h-12 rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STAGES.map((s, i) => <SelectItem key={s.title} value={String(i)} data-testid={`stage-option-${i + 1}`}>{i + 1}. {s.title}</SelectItem>)}
          </SelectContent>
        </Select>
        <DialogFooter className="gap-2">
          <Btn variant="outline" data-testid="update-stage-cancel" onClick={() => onOpenChange(false)}>Cancel</Btn>
          <Btn variant="gold" data-testid="update-stage-save" onClick={save}>Save stage</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function CaseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [stageOpen, setStageOpen] = useState(false);
  const isClient = user.user_type === "client";
  const c = CASES.find((x) => x.id === id && (!isClient || x.clientId === "cl1"));
  if (!c) return <Navigate to="/cases" replace />;

  return (
    <div>
      <Link to="/cases" data-testid="back-to-cases" className="mb-4 inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-slate-500 hover:text-navy"><ArrowLeft size={16} />Back to cases</Link>
      <div className="rounded-3xl bg-navy p-5 sm:p-7 shadow-xl">
        <div className="flex flex-wrap items-center gap-2"><span className="text-xs font-semibold text-amber-400">{c.number}</span><StageBadge stage={c.stage} /></div>
        <h1 data-testid="case-title" className="mt-2 font-heading text-2xl font-bold text-white sm:text-3xl">{c.title}</h1>
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <Info icon={Landmark} label="Court" value={c.court} />
          <Info icon={User} label="Client" value={c.clientName} />
          <Info icon={CalendarDays} label="Next hearing" value={c.nextHearing} />
        </div>
        {!isClient && (
          <div className="mt-6 flex flex-wrap gap-2">
            <Btn variant="gold" data-testid="update-stage-button" onClick={() => setStageOpen(true)}><RefreshCw size={16} />Update Stage</Btn>
            <Btn data-testid="add-note-button" className="bg-white/10 hover:bg-white/20" onClick={() => toast("Demo mode — notes are visual only.")}><NotebookPen size={16} />Add Note</Btn>
          </div>
        )}
      </div>

      {isClient && (
        <div data-testid="what-next-banner" className="mt-5 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <Lightbulb size={20} className="mt-0.5 shrink-0 text-gold" />
          <p className="text-sm text-amber-900"><span className="font-semibold">What happens next:</span> {NEXT_LINES[c.stage]}</p>
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="p-5 sm:p-6 lg:col-span-2">
          <h2 className="font-heading text-lg font-semibold text-navy">Case Journey</h2>
          <p className="mb-5 mt-1 text-sm text-slate-500">Every step of a criminal case, explained simply.</p>
          <CaseJourney caseData={c} />
        </Card>
        <Card className="h-fit p-5 sm:p-6">
          <h2 className="font-heading text-lg font-semibold text-navy">Case Updates</h2>
          <ul data-testid="case-updates-feed" className="mt-4 flex flex-col gap-4">
            {c.updates.map((u, i) => (
              <li key={u.text} className="relative flex gap-3 pl-1">
                <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${i === 0 ? "bg-gold" : "bg-slate-300"}`} />
                <div><div className="text-sm font-medium text-slate-700">{u.text}</div><div className="mt-0.5 text-xs text-slate-400">{u.date}</div></div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      {!isClient && <StageDialog open={stageOpen} onOpenChange={setStageOpen} current={c.stage} />}
    </div>
  );
}
